import { useState, useCallback, useEffect, useRef } from 'react';
import { trackingService } from '../services/trackingService';
import type { UserActivityItem, UseUserActivityReturn } from '../types/tracking';

export function useUserActivity(date: string, programs: string[], locationStatus?: string): UseUserActivityReturn {
  const [activities, setActivities] = useState<UserActivityItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Stable string key so useEffect fires on value change, not reference change
  const programsKey = programs.join(',');

  // Ref keeps the latest programs accessible inside the stable doFetch callback
  const programsRef = useRef(programs);
  programsRef.current = programs;

  const locationStatusRef = useRef(locationStatus);
  locationStatusRef.current = locationStatus;

  // Track the controller of the in-flight request so we can abort it when a new fetch starts
  const abortControllerRef = useRef<AbortController | null>(null);

  // doFetch is stable (no deps that change); it reads programs via the ref
  const doFetch = useCallback(async (currentDate: string, currentPrograms: string[], currentStatus?: string) => {
    // Cancel any in-flight request before starting a new one
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      setLoading(true);
      setError(null);

      const data = await trackingService.getUserActivity(
        currentDate,
        currentPrograms.length > 0 ? currentPrograms : undefined,
        currentStatus || undefined,
        controller.signal
      );

      // If this request was superseded, ignore its result
      if (controller.signal.aborted) return;

      setActivities(data);
    } catch (err: any) {
      // Ignore cancellation errors — a newer request is now in-flight
      if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED' || controller.signal.aborted) {
        return;
      }
      const message =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        'Failed to load user activity';
      setError(message);
      setActivities([]);
    } finally {
      // Only clear loading if this controller is still the active one
      if (abortControllerRef.current === controller) {
        setLoading(false);
      }
    }
  }, []);

  // Fire whenever date, programs, or locationStatus change
  useEffect(() => {
    doFetch(date, programsRef.current, locationStatusRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, programsKey, locationStatus]);

  // Cancel any in-flight request when the component unmounts
  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  const refetch = useCallback(() => {
    doFetch(date, programsRef.current, locationStatusRef.current);
  }, [date, doFetch]);

  return { activities, loading, error, refetch };
}
